import CtaButtonGroup, { CtaType } from "@/components/molecules/CtaButtonGroup";
import classNames from "classnames";
import SectionHeader from "./shared/SectionHeader";

/**
 * CenteredMediaBleedSection — centered eyebrow + 2-line title + subtitle
 * + CTA pair, with a large bleed media image below sitting on a soft
 * gradient wash. Used by feature V2 pages for "CREATE" and "AUTOMATE"
 * blocks where a full-bleed product screenshot anchors the story.
 *
 * Wash theme is configurable:
 *   - "pink"  — peach→pink radial wash (matches product-hero band)
 *   - "blue"  — soft purple/blue wash
 *   - "none"  — no wash, white background
 */

export type CenteredMediaSectionData = {
  eyebrow?: string;
  titleMuted?: string;
  titleBright?: string;
  title?: string;
  subtitle?: string;
  ctas?: CtaType[];
  image?: string;
  imageAlt?: string;
  washTheme?: "pink" | "blue" | "none";
};

const washStyles: Record<"pink" | "blue" | "none", string | undefined> = {
  pink:
    "linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,228,214,0.55) 45%, rgba(255,228,214,0.35) 100%)," +
    "radial-gradient(ellipse 55% 80% at 0% 100%, rgba(255,180,210,0.65) 0%, rgba(255,180,210,0.1) 55%, rgba(255,255,255,0) 85%)," +
    "radial-gradient(ellipse 55% 80% at 100% 100%, rgba(255,210,160,0.55) 0%, rgba(255,210,160,0.1) 55%, rgba(255,255,255,0) 85%)",
  blue:
    "linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(224,228,255,0.65) 45%, rgba(224,228,255,0.4) 100%)," +
    "radial-gradient(ellipse 65% 85% at 50% 100%, rgba(137,151,255,0.55) 0%, rgba(137,151,255,0.12) 55%, rgba(255,255,255,0) 85%)",
  none: undefined,
};

const CenteredMediaBleedSection = ({
  eyebrow,
  titleMuted,
  titleBright,
  title,
  subtitle,
  ctas,
  image,
  imageAlt,
  washTheme = "none",
}: CenteredMediaSectionData) => {
  const wash = washStyles[washTheme];
  return (
    <section className="relative w-full overflow-hidden bg-white">
      {wash ? (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 bottom-0 z-0 h-[70%]"
          style={{ background: wash }}
        />
      ) : null}
      <div
        className={classNames(
          "container relative z-10 mx-auto px-4 sm:px-6 md:px-8 lg:px-12",
          image ? "pt-10 md:pt-20 lg:pt-24" : "py-10 md:py-20 lg:py-24"
        )}
      >
        <SectionHeader
          eyebrow={eyebrow}
          titleMuted={titleMuted}
          titleBright={titleBright}
          title={title}
          subtitle={subtitle}
          ctas={ctas}
          variant="light-teal"
          align="center"
        />
        {image ? (
          <div className="mx-auto mt-12 w-full max-w-3xl md:mt-16">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={image}
              alt={imageAlt ?? ""}
              className={classNames(
                "block w-full",
                washTheme === "none" ? "rounded-t-3xl" : ""
              )}
            />
          </div>
        ) : null}
      </div>
    </section>
  );
};

export default CenteredMediaBleedSection;
